import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SEO from '../components/SEO';
import StructuredData, { generatePersonSchema } from '../components/StructuredData';
import './AboutPage.css';

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
    const paragraphRefs = useRef([]);
    const titleRef = useRef(null);

    // GSAP fade-up animation for title
    useEffect(() => {
        if (titleRef.current) {
            gsap.fromTo(
                titleRef.current,
                {
                    opacity: 0,
                    y: 50
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.5,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: 'top 85%',
                        toggleActions: 'play none none none'
                    }
                }
            );
        }
    }, []);

    // GSAP fade-up animation for paragraphs
    useEffect(() => {
        const timer = setTimeout(() => {
            paragraphRefs.current.forEach((ref, index) => {
                if (ref) {
                    gsap.fromTo(
                        ref,
                        {
                            opacity: 0,
                            y: 50
                        },
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.5,
                            ease: 'power2.out',
                            scrollTrigger: {
                                trigger: ref,
                                start: 'top 85%',
                                toggleActions: 'play none none none'
                            },
                            delay: index * 0.05 // Stagger animation
                        }
                    );
                }
            });
        }, 0);

        return () => {
            clearTimeout(timer);
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, []);

    return (
        <>
            <SEO
                title="About | Creative Technologist & 3D Artist | Jacob Faulkner"
                description="Jacob Faulkner (Ordos) is a creative technologist and 3D artist working between physical space, digital systems, and human experience. Specializing in interactive installations, projection mapping, XR, and experimental media."
                keywords="Jacob Faulkner, Ordos, creative technologist, 3D artist, interactive installations, projection mapping, VR, XR, Bristol, UK, freelance"
                url="/about"
            />
            <StructuredData data={generatePersonSchema()} />
            <div className="about-page">
            <div className="about-content">
                {/* Main About Section */}
                <section className="about-section">
                    <h1 ref={titleRef}>Creative Technologist & 3D Artist</h1>
                    
                    <div className="about-text">
                        <p 
                            ref={el => paragraphRefs.current[0] = el}
                            className="about-paragraph"
                        >
                            I'm Jacob Faulkner (<a href="https://www.ordos.co.uk" target="_blank" rel="noopener noreferrer" className="about-link">Ordos</a>) — a creative technologist and 3D artist working between physical space, digital systems, and human experience.
                        </p>
                        
                        <p 
                            ref={el => paragraphRefs.current[1] = el}
                            className="about-paragraph"
                        >
                            My background spans computer science, sound design, and hands-on technical production, and my work moves fluidly between interactive installations, projection mapping, XR, game development, and experimental media. I often combine visuals, sound, electronics, and real-time software to build systems that respond to people, place, and data.
                        </p>
                        
                        <p 
                            ref={el => paragraphRefs.current[2] = el}
                            className="about-paragraph"
                        >
                            Alongside commercial and cultural projects, I've carried out extensive 3D scanning and spatial documentation, from ancient ruins in Cambodia and historic European architecture to war bunkers and contemporary urban environments. This practice informs a wider interest in memory, surveillance, preservation, and how digital tools frame reality.
                        </p>
                        
                        <p 
                            ref={el => paragraphRefs.current[3] = el}
                            className="about-paragraph"
                        >
                            I've worked on large-scale projections and interactive installations for festivals including Glastonbury, Boomtown, and Shambala, as well as live touring visuals and performance systems. I'm the co-founder of Backhand, a Bristol-based exhibition platform, and the creator of Turbo VR, an experimental virtual reality game exploring labour, waste, and public space.
                        </p>
                        
                        <p 
                            ref={el => paragraphRefs.current[4] = el}
                            className="about-paragraph"
                        >
                            My practice increasingly incorporates AI systems, local language models, Stable Diffusion workflows, web development, cybersecurity research, and OSINT techniques — using these tools critically rather than cosmetically.
                        </p>
                    </div>
                </section>

                {/* Footer */}
                <footer className="about-footer">
                    <p>© <span id="year"></span> Jacob Faulkner All rights reserved.</p>
                </footer>
            </div>
        </div>
        </>
    );
};

export default AboutPage; 